// The "Add to album" picker (ADR-0059) — opened from the gallery's Select-mode bar to add the
// current Selection to an album in one call. Lists the Owner's existing albums (tap one to add) and
// offers a "New album" that creates-then-adds. Renders through the shared <Modal> (focus trap +
// Escape + backdrop, gated so it can't close mid-add). Kept a self-contained component so MediaGrid
// stays presentational: MediaGrid just mounts it with the client + the selected keys.

import { useEffect, useState } from "react";
import { type ApiClient, type AlbumSummary, ApiError } from "../api";
import { Modal } from "./Modal";
import { Loading } from "./Loading";
import { ErrorState } from "./ErrorState";

export interface AddToAlbumModalProps {
  client: ApiClient;
  /** The selected media keys to add. */
  keys: string[];
  /** Close without adding (Escape / backdrop / ×). */
  onClose: () => void;
  /** The keys were added — the grid clears its selection + closes (and can toast the result). */
  onAdded: (result: { albumId: string; name: string; added: number; total: number }) => void;
}

export function AddToAlbumModal({ client, keys, onClose, onAdded }: AddToAlbumModalProps): React.JSX.Element {
  const [albums, setAlbums] = useState<AlbumSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError(null);
    client
      .listAlbums()
      .then((a) => {
        if (live) {
          setAlbums(a);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (live) {
          setLoadError(e instanceof Error ? e.message : "Couldn’t load albums.");
          setLoading(false);
        }
      });
    return () => {
      live = false;
    };
  }, [client, reloadKey]);

  const fail = (e: unknown, fallback: string): void => {
    // A lapsed Session surfaces via the shell's watchUnauthorized wrapper (it rethrows), so here
    // we only need the human message for the in-modal error line.
    setActionError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : fallback);
    setBusy(false);
  };

  async function addTo(albumId: string, name: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const { added, total } = await client.addToAlbum(albumId, keys);
      onAdded({ albumId, name, added, total });
    } catch (e) {
      fail(e, "Couldn’t add to the album.");
    }
  }

  async function createAndAdd(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const { id } = await client.createAlbum(name);
      const { added, total } = await client.addToAlbum(id, keys);
      onAdded({ albumId: id, name, added, total });
    } catch (e) {
      fail(e, "Couldn’t create the album.");
    }
  }

  return (
    <Modal
      label="Add to album"
      onClose={onClose}
      testId="album-picker"
      className="album-picker-overlay"
      canClose={() => !busy}
      closeDisabled={busy}
    >
      <div className="album-picker">
        <h2 className="album-picker__title">
          Add {keys.length} {keys.length === 1 ? "item" : "items"} to an album
        </h2>

        <form className="album-picker__create" onSubmit={(e) => void createAndAdd(e)}>
          <input
            className="album-picker__input"
            type="text"
            value={newName}
            maxLength={60}
            placeholder="New album name"
            aria-label="New album name"
            data-testid="album-picker-new-name"
            onChange={(e) => setNewName(e.target.value)}
          />
          <button
            type="submit"
            className="btn btn--primary"
            data-testid="album-picker-create"
            disabled={!newName.trim() || busy}
          >
            New album
          </button>
        </form>

        {actionError && (
          <p className="album-picker__error" role="alert" data-testid="album-picker-error">
            {actionError}
          </p>
        )}

        {loading ? (
          <Loading label="Loading albums…" />
        ) : loadError ? (
          <ErrorState
            title="Couldn’t load albums"
            message={loadError}
            onRetry={() => setReloadKey((k) => k + 1)}
            headingLevel={3}
          />
        ) : albums.length === 0 ? (
          <p className="album-picker__empty" data-testid="album-picker-empty">
            No albums yet — create one above.
          </p>
        ) : (
          <ul className="album-picker__list" data-testid="album-picker-list">
            {albums.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="album-picker__item"
                  data-testid="album-picker-item"
                  disabled={busy}
                  onClick={() => void addTo(a.id, a.name)}
                >
                  <span className="album-picker__name">{a.name}</span>
                  <span className="album-picker__count">
                    {a.itemCount} {a.itemCount === 1 ? "item" : "items"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
